import { Link, useLocation } from "react-router";
import { Home, List, KeyRound, Receipt, Mail } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { cn } from "@/lib/utils";
import { useNativeTabs } from "@/lib/native-tabs-context";
import { useEffect } from "react";

const navItems = [
  { href: "/", label: "Dashboard", icon: Home },
  { href: "/blocks", label: "Blocks", icon: List },
  { href: "/email", label: "Messages", icon: Mail },
  { href: "/codes", label: "Codes", icon: KeyRound },
  { href: "/expenses", label: "Expenses", icon: Receipt },
];

interface MobileNavProps {
  className?: string;
  currentTabIndex?: number;
}

export function MobileNav({ className, currentTabIndex = -1 }: MobileNavProps) {
  const location = useLocation();
  const unreadCount = useQuery(api.email.sync.getUnreadCount) ?? 0;
  const { isNativeTabsActive, setEmailBadge } = useNativeTabs();

  // Sync unread count to native tabs badge
  useEffect(() => {
    setEmailBadge(unreadCount);
  }, [unreadCount, setEmailBadge]);

  // Hide on add block page and sign in
  if (location.pathname === "/blocks/new" || location.pathname === "/signin") {
    return null;
  }

  // Hide web nav when native iOS 26 tabs are active
  if (isNativeTabsActive) {
    return null;
  }

  const isOnTabPage = currentTabIndex >= 0;

  return (
    <nav
      className={cn(
        "sm:hidden fixed bottom-0 left-0 right-0 glass-thick border-t border-foreground/10 z-30",
        className
      )}
    >
      {/* Page indicator dots - 5 dots for 5 tabs */}
      {isOnTabPage && (
        <div className="flex justify-center gap-1.5 pt-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={cn(
                "h-1 rounded-full transition-all duration-200",
                i === currentTabIndex
                  ? "w-4 bg-primary"
                  : "w-1 bg-muted-foreground/30"
              )}
            />
          ))}
        </div>
      )}

      <div className="grid grid-cols-5 text-sm">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;
          const showBadge = item.href === "/email" && unreadCount > 0;
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "flex flex-col items-center py-3 relative",
                isActive ? "text-primary" : "text-muted-foreground"
              )}
            >
              <div className="relative">
                <Icon className="h-5 w-5" />
                {showBadge && (
                  <span className="absolute -top-1 -right-2 bg-destructive text-destructive-foreground text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </div>
              <span className="text-xs">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
